import { useEffect, useMemo, useState } from "react";

import {
  useDatasetRemoveNulls,
  useMutationPatchDatasetMetadata,
  useMutationPatchDatasetSchema,
  useTranslateDatasetGeometry
} from "@/api/dataset/useDatasetAPI";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import {
  SINGLE_GEOMETRY_OPTIONS,
  VALUE_TO_LABEL
} from "@/components/datasetManagement/details/dataSettingOptions";
import NotificationContent from "@/components/notification/notificationContent";
import { ROW_ID_DETAIL_DATASET } from "@/constants/dataset";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import { isAdminAreaLevel } from "@/helpers/datasetAttributeHelper";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { getErrorReasonMessage } from "@/locales/errorMessages";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetMetadata,
  DatasetSchemaLabel,
  DatasetSchemaPayload
} from "@/types/dataset";
import {
  DataSettings,
  EDataSettingPointType,
  EDataSettingPopupType,
  EDataSettingSpatialType
} from "@/types/dataSetting";
import type { CustomError } from "@/utils/errors";

import { useConfirmationModal } from "../ui/modal/useConfirmationModal";
import usePrevious from "../usePrevious";

/** Columns an ADM Area mapping produced. They are its output, never a source of
 * codes, and the BE hands them all one placeholder id — so they are kept out of
 * the attribute list, and their presence is what says a mapping exists. The
 * "Adm Area Geometry" of an ADM Area aggregation carries the same code but is
 * spatial, and is not a mapping. */
const isAdmAreaDerivedColumn = (column: DatasetAttributeSchema): boolean =>
  isAdminAreaLevel(column.attribute_code) && !column.is_spatial;

/** Puts the saved geometry column in the field of its aggregation type. An ADM
 * Area gets nothing: its reference column is the generated geometry, and the
 * code column it came from is not saved anywhere. */
const getSavedAttributeField = (
  aggregationType: string | undefined,
  attributeId: string | undefined
): Partial<NonNullable<DataSettings["spatialAggregation"]>> => {
  switch (aggregationType) {
    case EDataSettingSpatialType.Point:
      return { pointAttribute: attributeId };
    case EDataSettingSpatialType.Line:
      return { lineAttribute: attributeId };
    case EDataSettingSpatialType.H3:
      return { h3Attribute: attributeId };
    case EDataSettingSpatialType.Geohash:
      return { geohashAttribute: attributeId };
    case EDataSettingSpatialType.Admin_Area:
      return {};
    default:
      return { polygonAttribute: attributeId };
  }
};

type CoordinatePair = { latitudeId?: string; longitudeId?: string };

/** The latitude/longitude columns picked for a Point Coordinate, if that is the
 * picked aggregation. */
const getPickedCoordinate = (
  dataSetting: DataSettings | null
): CoordinatePair | null => {
  const spatialAggregation = dataSetting?.spatialAggregation;
  const isPointCoordinate =
    spatialAggregation?.type?.value === EDataSettingSpatialType.Point &&
    spatialAggregation.point?.pointType?.value ===
      EDataSettingPointType.Coordinate;
  if (!isPointCoordinate) return null;

  const coordinate = spatialAggregation.point?.pointCoordinate;
  return {
    latitudeId: coordinate?.latAttribute?.value,
    longitudeId: coordinate?.longAttribute?.value
  };
};

/** Which save put the highlight on the table. The two pick their columns
 * differently, so the reason has to outlive the request that set it. */
type HighlightReason = "newGeometry" | "admAreaMapping";

export const MESSAGES = {
  ALL_NULLS:
    "We couldn't generate any data. Please try again or check your input.",
  GENERATION_FAILED:
    "Sorry, we're experiencing an issue while processing your request. Please try again",

  NO_NULLS: "Country, Spatial Aggregation and Type successfully added. "
};

const PUBLISH_VALIDATION_MESSAGES = {
  DESCRIPTION: "Please add a description",
  DATA_SETTINGS: "Please set the data settings",
  REFERENCE_ID:
    "Please select ID, from table column before publish the data dataset by clicking cog icon on table header."
} as const;

export interface PublishValidationResult {
  canPublish: boolean;
  errors: {
    description?: string;
    dataSettings?: string;
    referenceId?: string;
  };
}
export interface UseDatasetDataSettingProps {
  setIsNullConfirmationOpen: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  id: string | undefined;
  metadata: DatasetMetadata | undefined;
  data: DatasetDetailRow[];
  dataSetting: DataSettings | null;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  onCloseSettingPopup: () => void;
  setRefetchStream: React.Dispatch<React.SetStateAction<boolean>>;
  /** The reworked Data Settings hub, where a spatial setting can be edited again. */
  isDataSettingsHubEnabled?: boolean;
}

export const useDatasetDataSetting = (props: UseDatasetDataSettingProps) => {
  const {
    setIsNullConfirmationOpen,
    setOpen,
    id,
    metadata,
    data,
    dataSetting,
    setDataSetting,
    onCloseSettingPopup,
    setRefetchStream,
    isDataSettingsHubEnabled = false
  } = props;
  const { showToast } = useToast();
  const { showConfirmation } = useConfirmationModal();
  const prevMetadata = usePrevious(metadata);

  const [nullAmount, setNullAmount] = useState<number>(0);
  const [highlightReason, setHighlightReason] =
    useState<HighlightReason | null>(null);
  const [publishValidation, setPublishValidation] =
    useState<PublishValidationResult>({ canPublish: false, errors: {} });
  const [dataSettingTemp, setDataSettingTemp] = useState<DataSettings | null>(
    dataSetting
  );
  // The lat/long pair the BE refused to turn into points. Its inline error
  // shows only while that same pair stays picked.
  const [rejectedCoordinate, setRejectedCoordinate] =
    useState<CoordinatePair | null>(null);

  // Bumped only where a save actually lands. A request that fails leaves it
  // alone, so nothing downstream mistakes "finished" for "saved".
  const [dataSettingSavedCount, setDataSettingSavedCount] = useState(0);
  const markDataSettingSaved = () =>
    setDataSettingSavedCount((count) => count + 1);

  const { mutate: mutatePatchDatasetMetadata, isPending: isPatchingMetadata } =
    useMutationPatchDatasetMetadata();
  const { mutate: mutatePatchDatasetCountry, isPending: isSavingCountry } =
    useMutationPatchDatasetMetadata();
  const { mutate: mutatePatchDatasetSchema } = useMutationPatchDatasetSchema();
  const { mutate: mutateTranslateDatasetGeometry, isPending: isTranslating } =
    useTranslateDatasetGeometry(id as string, {
      onSuccess: (response) => {
        markDataSettingSaved();
        setRejectedCoordinate(null);
        // TODO: @dalladirosa will be reused after Nix handle the error
        // if (response.failed === response.total) {
        //   return showToast({
        //     type: EToastType.ERROR,
        //     message: (
        //       <div className="flex flex-col gap-1">
        //         <p className="text-sm font-bold text-txtHeading">
        //           Generation Failed
        //         </p>
        //         <p className="text-sm text-txtHeading">
        //           {ERROR_MESSAGE.ALL_NULLS}
        //         </p>
        //       </div>
        //     )
        //   });
        // }
        setRefetchStream(true);

        if (
          !(
            dataSettingTemp?.spatialAggregation?.type?.value ===
              EDataSettingSpatialType.Point &&
            dataSettingTemp?.spatialAggregation?.point?.pointType?.value ===
              EDataSettingPointType.Coordinate
          ) &&
          response.failed > 0
        ) {
          setNullAmount(response.failed);
          setIsNullConfirmationOpen(true);
          return;
        }

        handleHighlightInvalidatedGeometry();

        showToast({
          type: EToastType.SUCCESS,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Data Setting Saved
              </p>
              <p className="text-sm text-txtHeading">{MESSAGES.NO_NULLS}</p>
            </div>
          )
        });
      },
      onError(err) {
        // The request throws the CustomError itself (see translateDatasetGeometry),
        // so its HTTP status sits on the error, not on the declared `message`.
        const status = (err as unknown as CustomError).code;
        const pickedCoordinate = getPickedCoordinate(dataSettingTemp);
        // The hub shows a 400 for lat/long under the fields, not as a toast.
        if (isDataSettingsHubEnabled && status === 400 && pickedCoordinate) {
          setRejectedCoordinate(pickedCoordinate);
          return;
        }

        const resolveErrorMessage = () => {
          if (err.message.reason && getErrorReasonMessage(err.message.reason)) {
            return getDisplayErrorMessage(
              err.message,
              MESSAGES.GENERATION_FAILED
            );
          }
          return MESSAGES.GENERATION_FAILED;
        };

        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
              description={resolveErrorMessage()}
            />
          )
        });
      }
    });
  const { mutate: mutateDatasetRemoveNulls } = useDatasetRemoveNulls(
    id as string,
    {
      onSuccess: () => {
        markDataSettingSaved();
        setRefetchStream(true);

        setIsNullConfirmationOpen(false);
        handleHighlightInvalidatedGeometry();

        showToast({
          type: EToastType.SUCCESS,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Data Setting Saved
              </p>
              <p className="text-sm text-txtHeading">
                Country and Spatial Aggregation successfully added, and{" "}
                {nullAmount} rows with null values have been removed.
              </p>
            </div>
          )
        });
      },
      onError: (error) => {
        showToast({
          type: EToastType.ERROR,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Something Went Wrong
              </p>
              <p className="text-sm text-txtHeading">
                Sorry, we're experiencing an issue while processing your
                request. Please try again
              </p>
              {import.meta.env.MODE !== "production" && (
                <p>
                  <b>Server message:</b> {JSON.stringify(error.message)}
                </p>
              )}
            </div>
          )
        });
      }
    }
  );

  const formattedAttributes = (schema: DatasetAttributeSchema[]) =>
    schema.map((item) => ({
      label: item?.attribute_name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: item?.id,
      description: item?.attribute_name
    }));

  const highlightGeoColumns = useMemo(() => {
    // A re-map overwrites its columns rather than adding them, so a diff
    // would come back empty. `attribute_code` finds them directly.
    if (highlightReason === "admAreaMapping") {
      return (metadata?.schema ?? []).filter(isAdmAreaDerivedColumn);
    }

    if (!prevMetadata?.schema || !metadata?.schema) return [];

    // Get all IDs from previous metadata
    const prevIds = new Set(prevMetadata.schema.map((item) => item.id));

    // Return items from current metadata that weren't in previous metadata
    return metadata.schema.filter((item) => !prevIds.has(item.id));
  }, [metadata, prevMetadata, highlightReason]);

  const attributes = useMemo(
    () => formattedAttributes(metadata?.schema ?? []),
    [metadata]
  );

  const { attributesLatitude, attributesLongitude } = useMemo(() => {
    const schema = metadata?.schema ?? [];

    // The hub offers every float column. A column labelled latitude goes to the
    // Latitude list only, one labelled longitude to the Longitude list only, and
    // an unlabelled float to both.
    if (isDataSettingsHubEnabled) {
      const getCoordinateAttributes = (
        ownLabel: DatasetSchemaLabel,
        otherLabel: DatasetSchemaLabel
      ) =>
        formattedAttributes(
          schema.filter(
            (item) =>
              item.data_label === ownLabel ||
              (item.data_type === DataSchemaType.FLOAT &&
                item.data_label !== otherLabel)
          )
        );
      return {
        attributesLatitude: getCoordinateAttributes(
          DatasetSchemaLabel.LATITUDE,
          DatasetSchemaLabel.LONGITUDE
        ),
        attributesLongitude: getCoordinateAttributes(
          DatasetSchemaLabel.LONGITUDE,
          DatasetSchemaLabel.LATITUDE
        )
      };
    }

    // Categorize attributes by data type
    // To reduce the number of iterations, we do it in a single pass
    const categorized = schema.reduce<{
      latitude: DatasetAttributeSchema[];
      longitude: DatasetAttributeSchema[];
      float: DatasetAttributeSchema[];
    }>(
      (acc, item) => {
        if (item.data_label === DatasetSchemaLabel.LATITUDE) {
          acc.latitude.push(item);
        } else if (item.data_label === DatasetSchemaLabel.LONGITUDE) {
          acc.longitude.push(item);
        } else if (item.data_type === DataSchemaType.FLOAT) {
          acc.float.push(item);
        }
        return acc;
      },
      {
        latitude: [],
        longitude: [],
        float: []
      }
    );

    // Use specific lat/long attributes if they exist, otherwise fall back to float
    const latitudeAttributes = categorized.latitude.length
      ? categorized.latitude
      : categorized.float;
    const longitudeAttributes = categorized.longitude.length
      ? categorized.longitude
      : categorized.float;

    return {
      attributesLatitude: formattedAttributes(latitudeAttributes),
      attributesLongitude: formattedAttributes(longitudeAttributes)
    };
  }, [metadata, isDataSettingsHubEnabled]);

  const pickedCoordinate = getPickedCoordinate(dataSettingTemp);
  const isCoordinateRejected = Boolean(
    rejectedCoordinate &&
      pickedCoordinate &&
      rejectedCoordinate.latitudeId === pickedCoordinate.latitudeId &&
      rejectedCoordinate.longitudeId === pickedCoordinate.longitudeId
  );

  const attributesForAdminArea = useMemo(
    () =>
      formattedAttributes(
        (metadata?.schema ?? []).filter(
          (item) =>
            (item.data_type === DataSchemaType.TEXT ||
              item.data_type === DataSchemaType.TEXT2 ||
              item.data_type === DataSchemaType.INT ||
              item.data_type === DataSchemaType.ID ||
              item.data_type === DataSchemaType.TITLE ||
              item.data_type === DataSchemaType.ADDRESS) &&
            item.attribute_name !== ROW_ID_DETAIL_DATASET &&
            !item.is_spatial &&
            !isAdmAreaDerivedColumn(item)
        )
      ),
    [metadata]
  );

  // A mapping the dataset already carries, from this session or an earlier one.
  const hasAdmAreaMapping = useMemo(
    () => (metadata?.schema ?? []).some(isAdmAreaDerivedColumn),
    [metadata]
  );

  const currentSpatialAggregationTypeFromMeta =
    metadata?.spatial_aggregation_type;
  const schemaFromMeta = metadata?.schema;

  const spatialAttributeIdFromMeta = useMemo(() => {
    return schemaFromMeta?.find((data) => data.is_reference_geo)?.id;
  }, [schemaFromMeta]);

  // Always the saved geometry column, so the label reads the same right after a
  // save and after a page refresh. For lat/long this is the generated column.
  const spatialAttributeLabel = useMemo(() => {
    const referenceGeo = schemaFromMeta?.find((data) => data.is_reference_geo);
    return referenceGeo
      ? formattedAttributes([referenceGeo])[0].label
      : undefined;
  }, [schemaFromMeta]);

  // To filter schema having is_spatial or geom
  const schemaIsSpatialOrGeom = useMemo(
    () => metadata?.schema?.filter((item) => item.is_spatial) ?? [],
    [metadata]
  );

  const dataSettingType = useMemo(() => {
    const geomSize = schemaIsSpatialOrGeom.length;

    if (geomSize > 1) return EDataSettingPopupType.Multiple;

    // In the hub, a saved spatial setting stays editable. Its single geometry
    // column (uploaded or generated) gets the Multiple picker instead of the
    // locked auto-select of a first save.
    if (
      geomSize === 1 &&
      isDataSettingsHubEnabled &&
      currentSpatialAggregationTypeFromMeta
    )
      return EDataSettingPopupType.Multiple;

    if (geomSize === 1) return EDataSettingPopupType.Single;

    return EDataSettingPopupType.None;
  }, [
    schemaIsSpatialOrGeom,
    isDataSettingsHubEnabled,
    currentSpatialAggregationTypeFromMeta
  ]);

  const computePublishValidation = (
    meta: DatasetMetadata | undefined
  ): PublishValidationResult => {
    if (!meta) {
      return {
        canPublish: false,
        errors: {
          description: PUBLISH_VALIDATION_MESSAGES.DESCRIPTION,
          dataSettings: PUBLISH_VALIDATION_MESSAGES.DATA_SETTINGS,
          referenceId: PUBLISH_VALIDATION_MESSAGES.REFERENCE_ID
        }
      };
    }

    if (meta.published) {
      return {
        canPublish: true,
        errors: {}
      };
    }

    const hasReferenceGeo =
      meta.schema?.some((column) => column.is_reference_geo) ?? false;
    const hasReferenceId =
      meta.schema?.some((column) => column.is_reference_id) ?? false;

    const errors: PublishValidationResult["errors"] = {};

    if (!meta.short_description?.trim()) {
      errors.description = PUBLISH_VALIDATION_MESSAGES.DESCRIPTION;
    }

    const hasDataSettingsConfigured =
      !!meta.country_id && !!meta.spatial_aggregation_type;

    if (!(hasDataSettingsConfigured && hasReferenceGeo)) {
      errors.dataSettings = PUBLISH_VALIDATION_MESSAGES.DATA_SETTINGS;
    }

    if (!hasReferenceId) {
      errors.referenceId = PUBLISH_VALIDATION_MESSAGES.REFERENCE_ID;
    }

    return {
      canPublish: Object.keys(errors).length === 0,
      errors
    };
  };

  const refreshPublishValidation = () => {
    const nextValidation = computePublishValidation(metadata);
    setPublishValidation(nextValidation);

    if (!nextValidation.canPublish) return false;

    return true;
  };

  const handleUnpublishDataset = () => {
    if (!id) {
      showToast({
        type: EToastType.ERROR,
        message: "ID is required to unpublish dataset"
      });
      return;
    }
    mutatePatchDatasetMetadata(
      { id, published: false },
      {
        onSuccess: () => {
          setRefetchStream(true);
          showToast({
            type: EToastType.SUCCESS,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Dataset Unpublished Successfully
                </p>
                <p className="text-sm text-txtHeading">
                  Your dataset is no longer published and is not available for
                  analysis
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onError() {
          showToast({
            type: EToastType.ERROR,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Something Went Wrong
                </p>
                <p className="text-sm text-txtHeading">
                  Sorry, we're experiencing an issue while unpublishing your
                  dataset. Please try again.
                </p>
              </div>
            )
          });
        },
        onSettled() {
          setOpen(false);
        }
      }
    );
  };

  const handlePublishDataset = () => {
    if (!id) {
      showToast({
        type: EToastType.ERROR,
        message: "ID is required to publish dataset"
      });
      return;
    }

    mutatePatchDatasetMetadata(
      { id, published: true },
      {
        onSuccess: () => {
          setRefetchStream(true);
          showToast({
            type: EToastType.SUCCESS,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Dataset Published Successfully
                </p>
                <p className="text-sm text-txtHeading">
                  Your data is now published and available for analysis.
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onError() {
          showToast({
            type: EToastType.ERROR,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Something Went Wrong
                </p>
                <p className="text-sm text-txtHeading">
                  Sorry, we're experiencing an issue while publishing your
                  dataset. Please try again.
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onSettled() {
          setOpen(false);
        }
      }
    );
  };

  const handleSaveDataSettingExistGeom = () => {
    if (!id || !dataSettingTemp?.country?.value) return;
    mutatePatchDatasetMetadata(
      {
        id,
        country_id: dataSettingTemp?.country?.value,
        spatial_aggregation_type:
          dataSettingTemp?.spatialAggregation?.type?.value
      },
      {
        onSuccess: () => {
          markDataSettingSaved();
          setDataSetting(dataSettingTemp);
          setRefetchStream(true);

          showToast({
            type: EToastType.SUCCESS,
            message: (
              <NotificationContent
                title={MESSAGE.DATA_SETTING_SAVED}
                description={MESSAGE.SAVE_DATA_SETTING_SUCCESS}
              />
            )
          });
        },
        onError: () => {
          showToast({
            type: EToastType.ERROR,
            message: (
              <NotificationContent
                title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
                description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
              />
            )
          });
        }
      }
    );
    // With type Multiple, need to call api to set as the primary/reference geo column
    if (dataSettingType !== EDataSettingPopupType.Multiple) return;

    const isRefGeoAttribute = () => {
      switch (dataSettingTemp?.spatialAggregation?.type?.value) {
        case EDataSettingSpatialType.Point:
          return dataSettingTemp?.spatialAggregation?.pointAttribute;
        case EDataSettingSpatialType.Polygon:
          return dataSettingTemp?.spatialAggregation?.polygonAttribute;
        case EDataSettingSpatialType.Line:
          return dataSettingTemp?.spatialAggregation?.lineAttribute;
        case EDataSettingSpatialType.H3:
          return dataSettingTemp?.spatialAggregation?.h3Attribute;
        case EDataSettingSpatialType.Geohash:
          return dataSettingTemp?.spatialAggregation?.geohashAttribute;
        default:
          return dataSettingTemp?.spatialAggregation?.pointAttribute;
      }
    };

    const attributeSelected = metadata?.schema.find(
      (item) => item.id === isRefGeoAttribute()
    ) as DatasetSchemaPayload;

    mutatePatchDatasetSchema({
      ...attributeSelected,
      id: attributeSelected.id,
      is_reference_geo: !!isRefGeoAttribute()
    });
  };

  const handlePublishButtonClick = () => {
    const isPublishable = refreshPublishValidation();

    if (!isPublishable) return false;

    return true;
  };

  // Saves just the country, as soon as it's picked in the Data Settings hub.
  const handleSaveCountry = (
    countryId: string,
    { onSaved, onNotSaved }: { onSaved: () => void; onNotSaved: () => void }
  ) => {
    if (!id) return;
    mutatePatchDatasetCountry(
      { id, country_id: countryId },
      {
        onSuccess: () => {
          onSaved();
          setRefetchStream(true);
          showToast({
            type: EToastType.SUCCESS,
            message: (
              <NotificationContent
                title={MESSAGE.DATA_SETTING_SAVED}
                description={MESSAGE.SAVE_DATA_SETTING_SUCCESS}
              />
            )
          });
        },
        onError: () => {
          onNotSaved();
          showToast({
            type: EToastType.ERROR,
            message: (
              <NotificationContent
                title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
                description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
              />
            )
          });
        }
      }
    );
  };

  const handleSaveDataSetting = () => {
    if (!dataSettingType || !id) return;
    // Check dataset is published or not, if published will show popup to confirm unpublish before change
    if (metadata?.published) {
      showConfirmation({
        title: "Unpublish Dataset?",
        message:
          MESSAGE.DATASET_MANAGEMENT_UNPUBLISH_DATASET_CONFIRMATION_DESCRIPTION,
        cancelLabel: "",
        confirmLabel: "Unpublish",
        onConfirm: () => handleUnpublishDataset()
      });
      return;
    }
    // Case dataset unpublished
    onCloseSettingPopup();

    // In the hub, an ADM Area picked among multiple geometries is generated from
    // a code column, like one picked for a dataset without geometry.
    const isAdmAreaPicked =
      isDataSettingsHubEnabled &&
      dataSettingType === EDataSettingPopupType.Multiple &&
      dataSettingTemp?.spatialAggregation?.type?.value ===
        EDataSettingSpatialType.Admin_Area;

    if (
      !isAdmAreaPicked &&
      [EDataSettingPopupType.Single, EDataSettingPopupType.Multiple].includes(
        dataSettingType
      )
    ) {
      handleSaveDataSettingExistGeom();
      return;
    }

    if (dataSettingType === EDataSettingPopupType.None || isAdmAreaPicked) {
      handleGenerateGeometry();
    }
  };

  const getAttributeIds = () => {
    const { spatialAggregation } = dataSettingTemp as DataSettings;
    let attributeIds: (string | undefined)[] = [];

    if (spatialAggregation?.type?.value === EDataSettingSpatialType.Point) {
      const pointType = spatialAggregation.point?.pointType?.value;

      if (pointType === "geocoding") {
        attributeIds = [spatialAggregation.point?.geocodingAtrtribute];
      } else {
        attributeIds = [
          spatialAggregation.point?.pointCoordinate?.latAttribute?.value,
          spatialAggregation.point?.pointCoordinate?.longAttribute?.value
        ];
      }
    } else {
      attributeIds = [spatialAggregation?.adminAreaAttribute];
    }

    return attributeIds;
  };

  const handleGenerateGeometry = () => {
    if (!dataSettingTemp) return;

    const { spatialAggregation, country } = dataSettingTemp;

    const attributeIds = getAttributeIds();

    mutateTranslateDatasetGeometry({
      geocoding:
        spatialAggregation?.type?.value === EDataSettingSpatialType.Point &&
        spatialAggregation?.point?.pointType?.value ===
          EDataSettingPointType.Geocoding,
      source_attribute_ids: attributeIds as string[],
      country_id: country?.value as string,
      spatial_aggregation_type: spatialAggregation?.type
        ?.value as SpatialAggregationType
    });
  };

  const handleRemoveNulls = () => {
    mutateDatasetRemoveNulls({
      attribute_id: highlightGeoColumns[0].id
    });
  };

  const handleHighlightInvalidatedGeometry = () => {
    setHighlightReason("newGeometry");
  };

  const handleHighlightAdmAreaMapping = () => {
    setHighlightReason("admAreaMapping");
  };

  /* If it has a single attribute, the system will automatically determine the appropriate
     spatial aggregation based on the geometry type and its attribute (user done need to select).
     - Point, if the geometry value is POINT(30 10)
     - Polygon, if the geometry value is POLYGON ((30 10, 40 40, 20 40, 10 20, 30 10)) or MULTIPOLYGON (((30 20, 45 40, 10 40, 30 20)), ((15 5, 40 10, 20 20, 15))
  */
  useEffect(() => {
    if (dataSettingType !== EDataSettingPopupType.Single) return;

    const mainSpatialAttribute = schemaIsSpatialOrGeom[0];

    if (!mainSpatialAttribute) return;

    let newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[0];

    // Option 1: If metadata already specifies a spatial_aggregation_type, respect it.
    if (metadata?.spatial_aggregation_type) {
      newAggregationTypeOption =
        SINGLE_GEOMETRY_OPTIONS.find(
          (item) => item.value === metadata.spatial_aggregation_type
        ) || SINGLE_GEOMETRY_OPTIONS[0];
    } else if (
      data?.length > 0 &&
      data[0] &&
      typeof data[0][mainSpatialAttribute.attribute_name] === "string"
    ) {
      // Option 2: Infer from the first row of data if metadata doesn't specify.
      const geometryValue = data[0][
        mainSpatialAttribute.attribute_name
      ] as string;
      if (
        geometryValue.includes("MULTIPOLYGON") ||
        geometryValue.includes("POLYGON")
      ) {
        // Assuming SINGLE_GEOMETRY_OPTIONS[1] is for Polygon types
        newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[1];
      } else if (geometryValue.includes("POINT")) {
        // Assuming SINGLE_GEOMETRY_OPTIONS[0] is for Point types
        newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[0];
      }
    }

    // Only update if the new type is different from the current one in dataSettingTemp
    // and newAggregationTypeOption is actually found.
    if (
      newAggregationTypeOption &&
      newAggregationTypeOption.value !==
        dataSettingTemp?.spatialAggregation?.type?.value
    ) {
      setDataSettingTemp((prev) => ({
        ...prev, // Handle null initial state for prev
        spatialAggregation: {
          ...prev?.spatialAggregation,
          type: newAggregationTypeOption
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSettingType, data, metadata, schemaIsSpatialOrGeom]);

  // This is for Multiple Geometry Setting, to handle the default aggregation type
  useEffect(() => {
    if (dataSettingType !== EDataSettingPopupType.Multiple) return;
    // The hub leaves a dataset that was never set without a type, so nothing
    // looks picked before the user picks it.
    if (isDataSettingsHubEnabled && !currentSpatialAggregationTypeFromMeta)
      return;

    setDataSettingTemp((prev) => ({
      ...prev, // Handle null initial state for prev
      spatialAggregation: {
        ...prev?.spatialAggregation,
        type: {
          label:
            VALUE_TO_LABEL[
              (currentSpatialAggregationTypeFromMeta as
                | "poi"
                | "polygon"
                | "adm_area"
                | "line"
                | "H3"
                | "geohash") ?? "polygon"
            ],
          value: currentSpatialAggregationTypeFromMeta ?? "polygon"
        },
        ...getSavedAttributeField(
          currentSpatialAggregationTypeFromMeta,
          spatialAttributeIdFromMeta
        )
      }
    }));
  }, [
    dataSettingType,
    isDataSettingsHubEnabled,
    currentSpatialAggregationTypeFromMeta,
    spatialAttributeIdFromMeta
  ]);

  // The columns only land once the dataset has been read again, so restart on
  // the count: five seconds from when they are actually on screen.
  const highlightColumnCount = highlightGeoColumns.length;
  useEffect(() => {
    if (!highlightReason) return;

    const timeout = setTimeout(() => {
      setHighlightReason(null);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [highlightReason, highlightColumnCount]);

  return {
    nullAmount,
    dataSettingTemp,
    dataSettingType,
    attributes,
    attributesLatitude,
    attributesLongitude,
    attributesForAdminArea,
    spatialAttributeLabel,
    isCoordinateRejected,
    hasAdmAreaMapping,
    highlightGeoColumns,
    isHighlightNewGeometry: highlightReason !== null,
    isTranslating,
    // Either request can be the one a save runs, depending on the popup type.
    isSavingDataSetting: isPatchingMetadata || isTranslating,
    dataSettingSavedCount,
    isSavingCountry,
    setDataSettingTemp,
    handleSaveCountry,
    handleHighlightInvalidatedGeometry,
    handleHighlightAdmAreaMapping,
    handleSaveDataSetting,
    handlePublishDataset,
    handleUnpublishDataset,
    handleRemoveNulls,
    publishValidation,
    handlePublishButtonClick
  };
};
